/** The query param the Subscriptions page reads to open the Details panel. */
export const SUBSCRIPTION_DETAILS_PARAM = 'details';

export const routes = {
  // Auth (tracking realm) — keys the auth pages/guards already reference.
  login: '/login',
  loginVerifyOtp: '/login/verify-otp',
  /** ADR-0029 — shown right after a successful login/OTP-verify when the phone number holds more
   *  than one linked account (Grower + Associate Grower). No route guard: a direct visit with no
   *  `accountSelectionRequired` session simply has nothing to pick and redirects home. */
  selectAccount: '/select-account',
  signup: '/signup',
  forgotPassword: '/forgot-password',
  forgotVerify: '/forgot-password/verify',
  forgotReset: '/forgot-password/reset',

  // Home redirects to /map — the Grower's landing page (113583 §1a).
  home: '/',

  devices: '/devices',
  device: (id = ':id') => `/devices/${id}`,
  /** The **Remap Device page** (119175 / 119176, ADO 119076). A **function**, not a literal, so the
   *  route table can pass the `:id` placeholder and every caller passes a real Device id —
   *  `routes.deviceRemap()` is the pattern, `routes.deviceRemap(device.id)` the link (ADR-0010).
   *  Reached only from the Device Details ellipsis menu; left only by Cancel or Save Mapping. */
  deviceRemap: (id = ':id') => `/devices/${id}/remap`,

  map: '/map',

  // Field-creation destinations for the Add Field panel's two options (113584 §11f). Both flows
  // are out of scope for feature 113582 (grilling Q4), so both land on a placeholder for now.
  fieldCreate: '/fields/new',
  /** Field Mapping mode on a Field that already exists (113601 / ADO 118557). A **function**, not a
   *  literal, so the route table can pass the `:fieldId` placeholder and every caller passes a real
   *  id — `routes.fieldEdit()` is the pattern, `routes.fieldEdit(field.id)` the link. */
  fieldEdit: (id = ':fieldId') => `/fields/${id}/edit`,
  fieldImport: '/fields/import',

  // Subscriptions (US-113569). The list is a route reached from Profile → Subscriptions; the
  // Subscription Details / Past Invoices surfaces are a right-hand side panel over it, not routes.
  subscriptions: '/subscriptions',
  /** Renew type-selection screen — Standard (1-Year) vs Bridge (December), US-113570. The Renewal
   *  Summary (US-113571) is a side panel over this screen, so it gets no route of its own. */
  subscriptionsRenew: '/subscriptions/renew',
  /** Renewal Checkout (US-114867 / US-115416) — fed from the in-memory renew-flow store. */
  subscriptionsCheckout: '/subscriptions/renew/checkout',
  /** Payment Completed (US-115416 § H) — the result of one renewal payment. A route, not navigation
   *  state, so a refresh re-reads `GET …/renewal/:renewalId` and shows the same screen. */
  subscriptionRenewal: (id = ':renewalId') => `/subscriptions/renewal/${id}`,
  /** Auto-Renewal Enrolment for one subscription (US-101879). Function form, as `fieldEdit`. */
  subscriptionAutoRenew: (id = ':id') => `/subscriptions/${id}/auto-renew`,
  /** The Subscriptions list with that subscription's Details panel open (101875 grilling Q11). */
  subscriptionDetails: (id: string) =>
    `/subscriptions?${SUBSCRIPTION_DETAILS_PARAM}=${encodeURIComponent(id)}`,

  support: '/support',
  ticket: (id = ':id') => `/support/${id}`,

  // Warranty — a notification for a warranty claim or a replacement request lands on the claim
  // (US-101849 § NAVIGATION: "that ticket/claim's detail page").
  warrantyClaims: '/warranty-claims',
  warrantyClaim: (id = ':id') => `/warranty-claims/${id}`,

  // Notification settings + center.
  notifications: '/notifications',

  profile: '/profile',
  // The rest of the profile menu (Figma 626:582182). Each is its own unpulled story, so the page
  // behind it is still a <PlaceholderPage> — the slug is provisional until that story lands.
  associateGrower: '/profile/associate-grower',
  manageAddress: '/profile/addresses',
  paymentMethods: '/profile/payment-methods',
  /** Terms & Conditions (US-113440). The consent line on Add Card links here (US-117846 §C) but
   *  intercepts the click and raises the same surface as an overlay, so the card details in the
   *  modal survive; the route is registered for direct access. Declared here rather than inline in
   *  a feature per ADR-0010. */
  terms: '/terms',

  notFound: '/404',
  serverError: '/500',
} as const;
